import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import ThemeToggle from '../ThemeToggle';
import useAppVersion from '../../utils/useAppVersion';
import MENU from './menuConfig';

const Icon = ({ name }) => (
	<svg className='navigation__icon'>
		<use xlinkHref={`/assets/sprite.svg#${name}`} />
	</svg>
);

// The one sidebar. Reads the shared menuConfig, gates by role/OT licence, and
// derives the active group/item from the URL (no per-screen currentMenu prop).
const Menu = ({ isAdmin = false, otLicensed = false, isProvider = false }) => {
	const appVersion = useAppVersion();
	const { pathname } = useLocation();

	const visible = (node) =>
		(!node.admin || isAdmin) && (!node.ot || otLicensed) && (!node.provider || isProvider);
	const isItemActive = (it) =>
		!it.external && (pathname === it.to || pathname.startsWith(`${it.to}/`));

	// Expand the group that holds the active link on load / navigation, and wire the
	// click-to-expand behavior the CSS expects on parent items.
	React.useEffect(() => {
		const active = document.querySelector('.navigation__sub__active');
		if (active) {
			const group = active.closest('.hasChildren');
			if (group) group.classList.add('subMenuActive');
		}
		const parents = document.getElementsByClassName('hasChildren');
		const handlers = [];
		for (let i = 0; i < parents.length; i++) {
			const el = parents[i];
			const h = () => el.classList.toggle('subMenuActive');
			el.addEventListener('click', h);
			handlers.push([el, h]);
		}
		return () => handlers.forEach(([el, h]) => el.removeEventListener('click', h));
	}, [pathname]);

	return (
		<nav className='navigation'>
			<div>
				<div className='navigation__logo_container'>
					<img src='/assets/logo-new.png' alt='logo' className='navigation__logo' />
					<img src='/assets/favicon.png' alt='favicon logo' className='navigation__logo2' />
					<h1 className='navigation__logo_heading'>{appVersion ? `Watcher ${appVersion}` : 'Watcher'}</h1>
				</div>

				<ul className='navigation__list'>
					{MENU.filter(visible).map((node) => {
						if (node.type === 'link') {
							const active = pathname === node.to;
							const slug = node.label.toLowerCase().replace(/\s+/g, '-');
							return (
								<div key={node.to} className={active ? 'currentNav ' : ''}>
									<li className='navigation__list--item'>
										<Link to={node.to} className='navigation__list--link' data-testid={`${slug}-btn-collapsed`}>
											<Icon name={node.icon} />&nbsp;
										</Link>
									</li>
									<li className='navigation__list--itemHover'>
										<Link to={node.to} className='navigation__list--link' data-testid={`${slug}-btn-open`}>
											<span className='navigation__list--link--span'>
												<Icon name={node.icon} />
												<span>{node.label}</span>
											</span>
										</Link>
									</li>
									{active && <div className='activeNav' data-testid={`${slug}-btn-activeNav`} />}
								</div>
							);
						}

						const items = (node.items || []).filter(visible);
						if (!items.length) return null;
						const active = items.some(isItemActive);
						return (
							<div key={node.label} className={active ? 'currentNav' : ''}>
								<li className='navigation__list--item'>
									<div className='navigation__list--link'>
										<Icon name={node.icon} />&nbsp;
									</div>
								</li>
								<li className='navigation__list--itemHover hasChildren'>
									<div className='navigation__list--link navigation__list-parent--link'>
										<span className='navigation__list--link--span'>
											<Icon name={node.icon} />
											<span>{node.label}</span>
										</span>
									</div>
									<ul>
										{items.map((it) => (
											<li key={it.to}>
												{it.external ? (
													<a href={it.to} target='_blank' rel='noopener noreferrer' className='navigation__list--link'>
														<Icon name='icon-chevron-right' />
														{it.label}
													</a>
												) : (
													<NavLink to={it.to} className={({ isActive }) => (isActive ? 'navigation__sub__active' : undefined)}>
														<Icon name='icon-chevron-right' />
														{it.label}
													</NavLink>
												)}
											</li>
										))}
									</ul>
								</li>
								{active && <div className='activeNav' />}
							</div>
						);
					})}
				</ul>
			</div>

			<div className='navigation__list--item'>
				<ThemeToggle />
			</div>

			<ul className='navigation__list' style={{ marginTop: '0', marginBottom: '3rem' }}>
				<div className='navigation__list--copyrightHidden'>
					<span>&nbsp;<br />&nbsp;</span>
				</div>
				<div className='navigation__list--copyright'>
					<span>{' '}</span>
				</div>
			</ul>
		</nav>
	);
};

export default Menu;
